/**
 * sku-search-fallback.js
 *
 * Drop this into your theme (e.g. assets/sku-search-fallback.js, loaded on
 * the search results page). Runs on EVERY search — it merges in any
 * SKU-prefix matches your local index finds (things like "MT204..." or
 * "FT301..." matching a typed "MT2" or "FT3") alongside whatever Shopify's
 * native search already found, skipping duplicates.
 *
 * ------------------------------------------------------------------
 * HOW IT WORKS
 * ------------------------------------------------------------------
 * 1. Page loads on /search?q=MT2
 * 2. Script fetches products.json (only once per visit, then cached)
 * 3. Finds every product with a SKU starting with the typed query
 * 4. Skips any product native search already displayed (no duplicates)
 * 5. Appends the extra matches below the native results
 * ------------------------------------------------------------------
 */

(function () {
  // The products.json URL is provided by Liquid automatically (see the
  // snippet you add to your theme — no need to manually find/copy this URL).
  // This fallback value only matters if you forget that step.
  const PRODUCTS_JSON_URL = window.SKU_SEARCH_JSON_URL || "";
  const MATCH_MODE = "startsWith"; // "startsWith" = MC6 matches MC6060...  |  "includes" = MC6 matches anywhere in the SKU

  let cachedProducts = null;

  function getSearchQuery() {
    const params = new URLSearchParams(window.location.search);
    return (params.get("q") || "").trim();
  }

  function getAlreadyShownHandles() {
    // Collect handles of products native search already displayed, so we
    // never show the same product twice when we merge in SKU matches.
    const shown = new Set();
    document.querySelectorAll('#items-grid .grid-item a[href*="/products/"]').forEach((a) => {
      const match = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (match) shown.add(match[1]);
    });
    return shown;
  }

  async function loadProductIndex() {
    if (cachedProducts) return cachedProducts;
    // COMPACT FORMAT: each entry is [handle, title, [sku1, sku2, ...]] —
    // one entry per product, not per variant, to keep the file small.
    const res = await fetch(PRODUCTS_JSON_URL, { cache: "force-cache" });
    if (!res.ok) throw new Error("Could not load product index: " + res.status);
    cachedProducts = await res.json();
    return cachedProducts;
  }

  function findMatches(query, products, excludeHandles) {
    const q = query.toLowerCase();
    const matches = [];
    for (const [handle, title, skus] of products) {
      if (excludeHandles.has(handle)) continue; // already shown by native search, skip
      const matchedSku = skus.find((sku) => {
        const skuLower = sku.toLowerCase();
        return MATCH_MODE === "startsWith" ? skuLower.startsWith(q) : skuLower.includes(q);
      });
      if (matchedSku) {
        matches.push({ handle, title, sku: matchedSku });
        if (matches.length >= 48) break; // cap results shown
      }
    }
    return matches;
  }

  async function attachImages(matches) {
    // Fetch product image only for the small set of actual matches
    // (not all 24,000+ products), using Shopify's lightweight per-product
    // JSON endpoint. Runs in parallel, capped by the 48-result limit above.
    await Promise.all(matches.map(async (m) => {
      try {
        const res = await fetch(`/products/${m.handle}.js`);
        if (!res.ok) return;
        const data = await res.json();
        m.image = data.featured_image || (data.images && data.images[0]) || null;
      } catch (e) {
        // If an image fetch fails, just skip it — not worth blocking results over.
      }
    }));
    return matches;
  }

  function renderResults(matches, query) {
    // Insert fallback results right after your theme's product grid
    // container (#ProductGridContainer, from main-search.liquid).
    let container = document.querySelector('#sku-fallback-results');

    if (matches.length === 0) {
      if (container) container.remove(); // nothing extra to add — leave native results as-is
      return;
    }

    if (!container) {
      container = document.createElement('div');
      container.id = 'sku-fallback-results';
      const anchor = document.querySelector('#ProductGridContainer') || document.querySelector('#items-grid') || document.body;
      anchor.appendChild(container);
    }

    container.innerHTML = `
      <p style="margin: 1em 0; font-size: 0.9em; opacity: 0.7;">
        More results matching SKU "${query}"
      </p>
      <div style="display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:1rem;">
        ${matches.map(p => `
          <a href="/products/${p.handle}" style="text-decoration:none; color:inherit;">
            ${p.image ? `<img src="${p.image}" alt="${p.title}" style="width:100%; height:auto; display:block;">` : ""}
            <div style="font-size:0.85em; margin-top:0.4em;">${p.title}</div>
            <div style="font-size:0.75em; opacity:0.6;">${p.sku}</div>
          </a>
        `).join("")}
      </div>
    `;
  }

  async function init() {
    const query = getSearchQuery();
    if (!query) return;
    if (!PRODUCTS_JSON_URL) {
      console.error("SKU fallback search: PRODUCTS_JSON_URL not set — check that the Liquid snippet setting window.SKU_SEARCH_JSON_URL was added before this script loads.");
      return;
    }

    try {
      const products = await loadProductIndex();
      const alreadyShown = getAlreadyShownHandles();
      const matches = findMatches(query, products, alreadyShown);
      renderResults(matches, query); // show extra matches immediately (fast, no images yet)
      if (matches.length > 0) {
        await attachImages(matches); // then fetch images for just these matches
        renderResults(matches, query); // re-render with images once they arrive
      }
    } catch (err) {
      console.error("SKU fallback search failed:", err);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
